import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { playersAPI } from '../services/api';
import PlayerList from '../components/players/PlayerList';
import PlayerForm from '../components/players/PlayerForm';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';

export default function Players() {
  const { isAuthenticated } = useAuth();
  const [players, setPlayers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load players on mount
  useEffect(() => {
    loadPlayers();
  }, []);

  const loadPlayers = async () => {
    try {
      setIsLoading(true);
      const response = await playersAPI.getAll();
      setPlayers(response.players || []);
    } catch (err) {
      console.error('Error loading players:', err);
      setError('Failed to load players');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPlayer = () => {
    setEditingPlayer(null);
    setShowModal(true);
  };

  const handleEditPlayer = (player) => {
    setEditingPlayer(player);
    setShowModal(true);
  };

  const handleDeletePlayer = async (player) => {
    if (!window.confirm(`Are you sure you want to delete ${player.name}?`)) {
      return;
    }

    try {
      await playersAPI.delete(player.id);
      await loadPlayers();
    } catch (err) {
      console.error('Error deleting player:', err);
      alert('Failed to delete player: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      setError('');

      if (editingPlayer) {
        await playersAPI.update(editingPlayer.id, formData);
      } else {
        await playersAPI.create(formData);
      }

      setShowModal(false);
      setEditingPlayer(null);
      await loadPlayers();
    } catch (err) {
      console.error('Error saving player:', err);
      setError(err.response?.data?.error || 'Failed to save player');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setEditingPlayer(null);
    setError('');
  };

  return (
    <div>
      {/* Header */}
      <div className="bg-white border-b border-gray-300 px-4 py-3 mb-4 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-gray-900">Players</h1>
        {isAuthenticated && (
          <button
            onClick={handleAddPlayer}
            className="text-sm text-primary hover:underline font-semibold"
          >
            + Add Player
          </button>
        )}
      </div>

      {error && !showModal && (
        <div className="bg-red-100 text-red-800 px-6 py-4 border border-red-300 mx-4 mb-4">
          {error}
        </div>
      )}

      <PlayerList
        players={players}
        isLoading={isLoading}
        onEdit={handleEditPlayer}
        onDelete={handleDeletePlayer}
      />

      {/* Add/Edit Player Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCancel}
        title={editingPlayer ? 'Edit Player' : 'Add New Player'}
        size="md"
      >
        {error && (
          <div className="bg-red-100 text-red-800 px-4 py-3 border border-red-300 mb-4">
            {error}
          </div>
        )}
        <PlayerForm
          player={editingPlayer}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isSubmitting}
        />
      </Modal>
    </div>
  );
}
